import { ICameraPermissionLabelSetting, ICommonSettings, ILanguageModel } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { PureComponent, ReactNode, createRef } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigCameraSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingCameraSetting?: ICameraPermissionLabelSetting;
  camera?: ICameraPermissionLabelSetting["values"];
}

export interface IConfigCameraSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingCameraId?: string;
  languages: ILanguageModel[];
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigCameraSettings extends PureComponent<IConfigCameraSettingsProps, IConfigCameraSettingsState> {
  formRef = createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigCameraSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingCameraSetting: undefined,
      camera: []
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigCameraSettingsProps) {
    if (prevProps.editingCameraId && !this.props.editingCameraId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingCameraId !== this.props.editingCameraId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingCameraId) {
      const setting = this.props.commonSettings.cameraPermissionLabelSettings?.find(
        (x) => x.id === this.props.editingCameraId
      );
      this.setState({ editingCameraSetting: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();
          fieldsValue.name = setting.name;
          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({ camera: setting.values });
        }
      });
    } else {
      const values: any[] = [];
      this.props.languages.forEach((language) => {
        values.push({
          languageName: language.shortName,
          cultureName: language.name,
          value: ""
        });
      });
      this.setState({ camera: values });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.cameraPermissionLabelSettings;

    const isSettingAlreadyExist =
      settings?.some((x) => x.name === value && x.id !== this.props.editingCameraId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Camera Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: ICameraPermissionLabelSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      values: this.state.camera?.map((camera) => ({
        ...camera,
        value: camera.value?.trim()
      }))
    };
    if (this.props.editingCameraId) {
      obj.id = this.props.editingCameraId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "cameraPermissionLabelSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "cameraPermissionLabelSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingCameraId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const parentDataKey = this.props.formInputName[0] === "androidConfig" ? "iosConfig" : "androidConfig";
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingCameraId || "",
      configKey: "cameraPermissionLabelSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Camera setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [[parentDataKey, this.props.formInputName[1]]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
          This Camera setting is
          used on ${
            this.props.formInputName[0] === "androidConfig" ? "IOS" : "Android"
          } section. Please discard this setting
          on other config section before delete.
        `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  setValue = (e, lang) => {
    const camera = _.cloneDeep(this.state.camera);
    camera?.forEach((value) => {
      if (value.cultureName === lang.cultureName) {
        value.value = e.target.value;
      }
    });
    this.setState({ camera });
  };

  getSectionDescription = (): ReactNode => {
    const contentKey =
      this.props.formInputName[0] === "androidConfig"
        ? "camerapermissionlabelssettingspopup_camerapermissionlabelssettings_description_android"
        : "camerapermissionlabelssettingspopup_camerapermissionlabelssettings_description_ios";

    return this.props.getSectionDescription(contentKey);
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Camera setting"
        secondHeader="Camera setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingCameraId ? this.deleteSetting : undefined}
            deleteTestId="cameraSettingModalDeleteButton"
            submitTestId={`cameraSettingModal${this.props.editingCameraId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingCameraId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configCameraForm"
          />
        }
      >
        <Form
          id="configCameraForm"
          name="cameraSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody description={this.getSectionDescription()}>
            <ConfigField
              label="Name"
              required
              tooltipContentKey="camerapermissionlabelssettingspopup_camerapermissionlabelssettings_name"
            >
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Camera Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Camera Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="cameraSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            {this.state.camera?.map((value) => {
              return (
                <ConfigField
                  key={value.cultureName}
                  label={value.languageName}
                  tooltipContentKey="camerapermissionlabelssettingspopup_camerapermissionlabelssettings_english"
                >
                  <div style={{ width: 380, marginBottom: 24 }}>
                    <KuikaInput size="md" value={value.value} onChange={(e) => this.setValue(e, value)} />
                  </div>
                </ConfigField>
              );
            })}
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
